import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useDataSources } from '../hooks/useDataSources';
import { useChatbots } from '../hooks/useChatbots';
import Modal from '../components/Modal';
import modalStyles from '../components/Modal.module.scss';
import { DataSource } from '../services/dataSourceService';
// import FloatingChat from '../components/FloatingChat';
import InPageChat, { InPageChatHandle } from '../components/InPageChat';
import chatbotService from '../services/chatbotService';
import analyticsService, { AnalyticsOverview } from '../services/analyticsService';
import DataSourceSelectionModal from '../components/DataSourceSelectionModal';
import TermsOfServicePage from './TermsOfServicePage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import ContactSupportPage from './ContactSupportPage';
import AboutPage from './AboutPage';

// Chart components
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';

// Settings form validation helpers (dashboard)
const SETTINGS_PASSWORD_STRONG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const numberFormatter = new Intl.NumberFormat('en-US');

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function validateRequiredField(value: string, name: string, minLength = 1): string | null {
  const v = value.trim();
  if (!v) return `${name} is required`;
  if (minLength > 1 && v.length < minLength) return `${name} must be at least ${minLength} characters`;
  return null;
}

function validateStrongPassword(value: string): string | null {
  if (!value) return 'Password is required';
  if (!SETTINGS_PASSWORD_STRONG.test(value)) {
    return 'Password must be at least 8 characters and include uppercase, lowercase, and a number.';
  }
  return null;
}

function truncateLabel(value: string, maxLength: number): string {
  if (!value) return '';
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function percentDelta(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) {
    if (current === 0) return 0;
    // Prefer a deterministic chip value over blank state for zero baseline windows.
    return 100;
  }
  return ((current - previous) / previous) * 100;
}

function formatDeltaChip(delta: number | null): string {
  if (delta === null) return '—';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}%`;
}

type TimeRangeOption = 7 | 30 | 90;
const TIME_RANGE_OPTIONS: TimeRangeOption[] = [7, 30, 90];
const PERFORMANCE_MODES = ['speed', 'balance', 'accuracy'] as const;

function getPerformanceModeIndex(mode: string): number {
  const index = PERFORMANCE_MODES.indexOf(mode as (typeof PERFORMANCE_MODES)[number]);
  return index === -1 ? 1 : index;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateCompanyName, updatePassword } = useAuth();
  const isTermsPage = location.pathname === '/dashboard/terms';
  const isPrivacyPage = location.pathname === '/dashboard/privacy';
  const isContactPage = location.pathname === '/dashboard/contact';
  const isAboutPage = location.pathname === '/dashboard/about';
  const isStaticPage = isTermsPage || isPrivacyPage || isContactPage || isAboutPage;
  const staticPageTitle = isTermsPage ? 'Terms of Service' : isPrivacyPage ? 'Privacy Policy' : isContactPage ? 'Contact Support' : isAboutPage ? 'About' : '';
  const { theme, toggleTheme } = useTheme();
  const { 
    databaseSources, 
    fileSources, 
    loading, 
    uploadProgress,
    error, 
    fetchDataSources, 
    fetchFileSources, 
    fetchAllSources,
    createDataSource,
    deleteDataSourceById,
    uploadFiles
  } = useDataSources();
  
  const {
    chatbots,
    fetchChatbots,
    createChatbot,
    updateChatbot,
    deleteChatbot
  } = useChatbots();
  
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeSourceTab, setActiveSourceTab] = useState('databases');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Keep the existing sidebarExpanded state for mobile
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [databaseModalOpen, setDatabaseModalOpen] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [chatbotModalOpen, setChatbotModalOpen] = useState(false);
  const [editChatbotModalOpen, setEditChatbotModalOpen] = useState(false);
  const [newChatbot, setNewChatbot] = useState({
    name: '',
    description: '',
    industry: '',
    useCase: '',
    tone: '',
    dataSourceIds: [] as string[],
    responseSource: 'auto'
  });
  const [editingChatbot, setEditingChatbot] = useState({
    id: '',
    name: '',
    description: '',
    industry: '',
    systemPrompt: '',
    status: '',
    responseSource: 'auto',
    performanceMode: 'balance'
  });
  const [newDataSource, setNewDataSource] = useState({
    name: '',
    type: 'postgresql',
    host: '',
    port: 5432,
    database: '',
    user: '',
    password: ''
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [inPageChatbot, setInPageChatbot] = useState<any | null>(null); // For in-page chat display
  const [, setInPageChatOpen] = useState(false); // Track if in-page chat is open
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dataSourceSelectionModalOpen, setDataSourceSelectionModalOpen] = useState(false);
  const [selectedChatbotForDataSources, setSelectedChatbotForDataSources] = useState<any>(null);
  const [initialDataSourceSelection, setInitialDataSourceSelection] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inPageChatRef = useRef<InPageChatHandle | null>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const chartRelayoutTimerRef = useRef<number | null>(null);
  const lastMainContentWidthRef = useRef<number>(0);
  const analyticsRequestIdRef = useRef(0);

  // Settings – profile & security forms
  const [passwordFormError, setPasswordFormError] = useState<string | null>(null);
  const [passwordFormSuccess, setPasswordFormSuccess] = useState<string | null>(null);
  const [profileFormError, setProfileFormError] = useState<string | null>(null);
  const [profileFormSuccess, setProfileFormSuccess] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>(user?.companyName || '');
  const [profileCompanyTouched, setProfileCompanyTouched] = useState(false);

  const [securityCurrentPassword, setSecurityCurrentPassword] = useState('');
  const [securityNewPassword, setSecurityNewPassword] = useState('');
  const [securityConfirmPassword, setSecurityConfirmPassword] = useState('');
  const [securityTouched, setSecurityTouched] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [activeSettingsTab, setActiveSettingsTab] = useState<'profile' | 'security'>('profile');
  const [analyticsOverview, setAnalyticsOverview] = useState<AnalyticsOverview | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [chartLayoutNonce, setChartLayoutNonce] = useState(0);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeOption>(30);

  const profileCompanyError = profileCompanyTouched
    ? validateRequiredField(companyName, 'Company name', 2)
    : null;
  // Enable button whenever value is valid (same pattern as Home auth forms)
  const profileFormValid = !validateRequiredField(companyName, 'Company name', 2);

  const securityCurrentError = securityTouched.current
    ? validateRequiredField(securityCurrentPassword, 'Current password')
    : null;
  const securityNewError = securityTouched.new ? validateStrongPassword(securityNewPassword) : null;
  const securityConfirmError = securityTouched.confirm
    ? securityConfirmPassword
      ? securityNewPassword === securityConfirmPassword
        ? null
        : 'New passwords do not match'
      : 'Confirm password is required'
    : null;

  const securityFormValid =
    !validateRequiredField(securityCurrentPassword, 'Current password') &&
    !validateStrongPassword(securityNewPassword) &&
    !!securityConfirmPassword.trim() &&
    securityNewPassword === securityConfirmPassword;

  useEffect(() => {
    setCompanyName(user?.companyName || '');
    setProfileCompanyTouched(false);
  }, [user?.companyName]);

  // Auto-clear settings feedback messages after 4 seconds so they
  // don't reappear when navigating back to the Settings tab.
  useEffect(() => {
    if (!profileFormError && !profileFormSuccess && !passwordFormError && !passwordFormSuccess) {
      return;
    }
    const timer = setTimeout(() => {
      setProfileFormError(null);
      setProfileFormSuccess(null);
      setPasswordFormError(null);
      setPasswordFormSuccess(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [profileFormError, profileFormSuccess, passwordFormError, passwordFormSuccess]);

  // Scroll to top when switching between Terms, Privacy, or Contact Support
  useEffect(() => {
    if (isStaticPage && mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname, isStaticPage]);

  useEffect(() => {
    if (activeTab === 'sources') {
      // Only fetch data based on which sub-tab is active
      if (activeSourceTab === 'databases') {
        fetchDataSources();
      } else if (activeSourceTab === 'files') {
        fetchFileSources();
      }
    } else if (activeTab === 'chatbots') {
      fetchChatbots();
    }
  }, [activeTab, activeSourceTab, fetchDataSources, fetchFileSources, fetchChatbots]);

  // Additional effect to fetch data when switching between sub-tabs
  useEffect(() => {
    if (activeTab === 'sources') {
      if (activeSourceTab === 'databases') {
        fetchDataSources();
      } else if (activeSourceTab === 'files') {
        fetchFileSources();
      }
    }
  }, [activeSourceTab, activeTab, fetchDataSources, fetchFileSources]);

  // Poll for file status updates while viewing file sources so
  // "processing" files automatically refresh to their final status
  useEffect(() => {
    if (activeTab !== 'sources' || activeSourceTab !== 'files') {
      return;
    }

    // Only poll when there are files still marked as processing
    const hasProcessingFiles = fileSources.some(
      (file) => file.status === 'processing'
    );

    if (!hasProcessingFiles) {
      return;
    }

    const intervalId = window.setInterval(() => {
      fetchFileSources();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeTab, activeSourceTab, fileSources, fetchFileSources]);

  const fetchAnalyticsOverview = useCallback(async () => {
    const requestId = ++analyticsRequestIdRef.current;
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      const overview = await analyticsService.getOverview(selectedTimeRange, true);
      if (requestId !== analyticsRequestIdRef.current) return;
      setAnalyticsOverview(overview);
    } catch (err: any) {
      if (requestId !== analyticsRequestIdRef.current) return;
      console.error('Failed to fetch analytics overview:', err);
      setAnalyticsError(err?.message || 'Failed to fetch analytics');
    } finally {
      if (requestId !== analyticsRequestIdRef.current) return;
      setAnalyticsLoading(false);
    }
  }, [selectedTimeRange]);

  useEffect(() => {
    if (isStaticPage) return;
    if (activeTab !== 'dashboard' && activeTab !== 'analytics') return;
    fetchAnalyticsOverview();
  }, [activeTab, isStaticPage, fetchAnalyticsOverview]);

  useEffect(() => {
    if (!inPageChatbot) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        inPageChatRef.current?.startNewChat();
        setSessionHistory([]);
        setSelectedSession(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inPageChatbot]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeTab !== 'dashboard' && activeTab !== 'analytics') return;
    if (typeof window === 'undefined') return;

    // Recharts can miss container size updates after sidebar transitions.
    // Trigger resize events across the transition window to force recalculation.
    window.dispatchEvent(new Event('resize'));
    const timers = [120, 260, 420].map((delay) =>
      window.setTimeout(() => window.dispatchEvent(new Event('resize')), delay)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [sidebarExpanded, sidebarCollapsed, activeTab, isMobileViewport]);

  useEffect(() => {
    const container = mainContentRef.current;
    if (!container) return;
    if (typeof ResizeObserver === 'undefined') return;
    lastMainContentWidthRef.current = Math.round(container.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = Math.round(entry.contentRect.width);
      const prevWidth = lastMainContentWidthRef.current;
      // Width changes drive chart layout; ignore height-only noise.
      if (Math.abs(nextWidth - prevWidth) < 2) return;
      lastMainContentWidthRef.current = nextWidth;

      if (chartRelayoutTimerRef.current) {
        window.clearTimeout(chartRelayoutTimerRef.current);
      }
      chartRelayoutTimerRef.current = window.setTimeout(() => {
        setChartLayoutNonce((prev) => prev + 1);
        window.dispatchEvent(new Event('resize'));
      }, 100);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (chartRelayoutTimerRef.current) {
        window.clearTimeout(chartRelayoutTimerRef.current);
        chartRelayoutTimerRef.current = null;
      }
    };
  }, []);

  const newChatShortcutLabel = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? '⌘N' : 'Ctrl+N';

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const closeSidebar = () => {
    setSidebarExpanded(false);
  };

  const showTab = (tabName: string) => {
    if (tabName !== 'chatbots') {
      setInPageChatbot(null);
    }
    setActiveTab(tabName);
    if (isStaticPage) {
      navigate('/dashboard');
    }
    closeSidebar();
  };

  const switchSourceTab = (tabName: string) => {
    setActiveSourceTab(tabName);
  };


  const openCreateModal = (type: string = 'chatbot') => {
    if (type === 'database') {
      setDatabaseModalOpen(true);
    } else if (type === 'files') {
      setFileUploadModalOpen(true);
    } else if (type === 'chatbot') {
      setChatbotModalOpen(true);
    } else {
      setCreateModalOpen(true);
    }
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setDatabaseModalOpen(false);
    setFileUploadModalOpen(false);
    setChatbotModalOpen(false);
    // Reset form fields
    setNewDataSource({
      name: '',
      type: 'postgresql',
      host: '',
      port: 5432,
      database: '',
      user: '',
      password: ''
    });
    setNewChatbot({
      name: '',
      description: '',
      industry: '',
      useCase: '',
      tone: '',
      dataSourceIds: [],
      responseSource: 'auto'
    });
    setSelectedFiles([]);
  };

  const handleLogout = () => {
    logout();
    navigate('/?auth=login');
  };

  const handleDataSourceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewDataSource(prev => ({
      ...prev,
      [name]: name === 'port' ? parseInt(value) || 0 : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const handleCreateDataSource = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createDataSource(newDataSource);
      // After creating a data source, make sure we're showing the databases tab
      setActiveSourceTab('databases');
      closeCreateModal();
    } catch (err) {
      console.error('Failed to create data source:', err);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;
    
    try {
      await uploadFiles(selectedFiles);
      // After uploading files, make sure we're showing the files tab
      setActiveSourceTab('files');
      closeCreateModal();
    } catch (err) {
      console.error('Failed to upload files:', err);
    }
  };

  const handleChatbotChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewChatbot(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateChatbot = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createChatbot({
        ...newChatbot,
        useCase: newChatbot.useCase,
        responseSource: newChatbot.responseSource
      });
      closeCreateModal();
    } catch (err) {
      console.error('Failed to create chatbot:', err);
    }
  };

  const handleEditChatbot = (chatbot: any) => {
    // Ensure we handle both possible property names for response source and validate the value
    let responseSource = chatbot.response_source || chatbot.responseSource || 'auto';
    
    // Validate that responseSource is one of the allowed values
    const validResponseSources = ['auto', 'datasource', 'llm'];
    if (!validResponseSources.includes(responseSource)) {
      responseSource = 'auto'; // Default to 'auto' if invalid value
    }
    
    let performanceMode = chatbot.performance_mode || chatbot.performanceMode || 'balance';
    if (!PERFORMANCE_MODES.includes(performanceMode)) {
      performanceMode = 'balance';
    }

    setEditingChatbot({
      id: chatbot.id,
      name: chatbot.name || '',
      description: chatbot.description || '',
      industry: chatbot.industry || '',
      systemPrompt: chatbot.system_prompt || '',
      status: chatbot.status || 'draft',
      responseSource: responseSource,
      performanceMode
    });
    setEditChatbotModalOpen(true);
  };


  const handleEditChatbotChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditingChatbot(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateChatbot = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        name: editingChatbot.name,
        description: editingChatbot.description,
        industry: editingChatbot.industry,
        status: editingChatbot.status,
        responseSource: editingChatbot.responseSource,
        performanceMode: editingChatbot.performanceMode
      };

      payload.systemPrompt = editingChatbot.systemPrompt;

      await updateChatbot(editingChatbot.id, payload);
      setEditChatbotModalOpen(false);
    } catch (err) {
      console.error('Failed to update chatbot:', err);
    }
  };

  const handleDeleteChatbot = async (id: string) => {
    // Create a custom confirmation modal that matches the website style
    const confirmDelete = () => {
      return new Promise<boolean>((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'custom-confirm-modal';
        modal.innerHTML = `
          <div class="custom-confirm-content">
            <div class="custom-confirm-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div class="custom-confirm-body">
              <p>Are you sure you want to delete this chatbot? This action cannot be undone.</p>
            </div>
            <div class="custom-confirm-footer">
              <button class="btn btn-secondary" id="cancel-btn">Cancel</button>
              <button class="btn btn-primary" id="confirm-btn" style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);">Delete</button>
            </div>
          </div>
        `;
        
        // Add styles for the custom confirm modal
        const style = document.createElement('style');
        style.textContent = `
          .custom-confirm-modal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.6);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            backdrop-filter: blur(4px);
          }
          
          .custom-confirm-content {
            background-color: var(--card-bg);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 30px;
            width: 90%;
            max-width: 500px;
            animation: slideUp 0.3s ease;
            margin: 0 auto;
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          .custom-confirm-header h3 {
            font-size: 20px;
            font-weight: 700;
            color: var(--text-primary);
            margin: 0 0 20px 0;
          }
          
          .custom-confirm-body p {
            color: var(--text-primary);
            margin: 0 0 24px 0;
            line-height: 1.5;
          }
          
          .custom-confirm-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
          }
        `;
        
        document.head.appendChild(style);
        document.body.appendChild(modal);
        
        const confirmBtn = modal.querySelector('#confirm-btn');
        const cancelBtn = modal.querySelector('#cancel-btn');
        
        const cleanup = () => {
          document.body.removeChild(modal);
          document.head.removeChild(style);
        };
        
        confirmBtn?.addEventListener('click', () => {
          cleanup();
          resolve(true);
        });
        
        cancelBtn?.addEventListener('click', () => {
          cleanup();
          resolve(false);
        });
      });
    };
    
    const confirmed = await confirmDelete();
    if (confirmed) {
      try {
        await deleteChatbot(id);
      } catch (err) {
        console.error('Failed to delete chatbot:', err);
      }
    }
  };

  const handleDeleteDataSource = async (id: string, isFile: boolean = false) => {
    // Create a custom confirmation modal that matches the website style
    const confirmDelete = () => {
      return new Promise<boolean>((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'custom-confirm-modal';
        modal.innerHTML = `
          <div class="custom-confirm-content">
            <div class="custom-confirm-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div class="custom-confirm-body">
              <p>Are you sure you want to delete this data source? This action cannot be undone.</p>
            </div>
            <div class="custom-confirm-footer">
              <button class="btn btn-secondary" id="cancel-btn">Cancel</button>
              <button class="btn btn-primary" id="confirm-btn" style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);">Delete</button>
            </div>
          </div>
        `;
        
        // Add styles for the custom confirm modal
        const style = document.createElement('style');
        style.textContent = `
          .custom-confirm-modal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.6);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            backdrop-filter: blur(4px);
          }
          
          .custom-confirm-content {
            background-color: var(--card-bg);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 30px;
            width: 90%;
            max-width: 500px;
            animation: slideUp 0.3s ease;
            margin: 0 auto;
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          .custom-confirm-header h3 {
            font-size: 20px;
            font-weight: 700;
            color: var(--text-primary);
            margin: 0 0 20px 0;
          }
          
          .custom-confirm-body p {
            color: var(--text-primary);
            margin: 0 0 24px 0;
            line-height: 1.5;
          }
          
          .custom-confirm-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
          }
        `;
        
        document.head.appendChild(style);
        document.body.appendChild(modal);
        
        const confirmBtn = modal.querySelector('#confirm-btn');
        const cancelBtn = modal.querySelector('#cancel-btn');
        
        const cleanup = () => {
          document.body.removeChild(modal);
          document.head.removeChild(style);
        };
        
        confirmBtn?.addEventListener('click', () => {
          cleanup();
          resolve(true);
        });
        
        cancelBtn?.addEventListener('click', () => {
          cleanup();
          resolve(false);
        });
      });
    };
    
    const confirmed = await confirmDelete();
    if (confirmed) {
      try {
        await deleteDataSourceById(id, isFile);
      } catch (err) {
        console.error('Failed to delete data source:', err);
      }
    }
  };

  const fetchChatHistory = async (chatbotId: string) => {
    setHistoryLoading(true);
    try {
      const response = await chatbotService.getChatHistory(chatbotId);
      // Sort chat history by timestamp in descending order (newest first)
      const sortedMessages = response.data.messages.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setChatHistory(sortedMessages);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchSessionHistory = async (sessionId: string, chatbotId: string) => {
    setHistoryLoading(true);
    try {
      const response = await chatbotService.getSessionHistory(sessionId, chatbotId);
      // Sort session history by timestamp in ascending order (oldest first)
      const sortedMessages = response.data.messages.sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      setSessionHistory(sortedMessages);
      setSelectedSession(sessionId);
    } catch (err) {
      console.error('Failed to fetch session history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAttachDataSources = async (chatbot: any) => {
    setSelectedChatbotForDataSources(chatbot);
    
    // Fetch fresh data sources when opening the modal
    const { databaseSources: freshDatabaseSources, fileSources: freshFileSources } = await fetchAllSources();
    
    // Extract just the IDs from the data_source_ids array
    // We need to add prefixes to match the format used in the DataSourceSelectionModal
    const dataSourceIds = (chatbot.data_source_ids || []).map((id: string) => {
      // Check if it's already prefixed
      if (id.startsWith('db-') || id.startsWith('file-')) {
        return id;
      }
      // If no prefix, we need to determine if it's a database or file source
      // Check if the id exists in databaseSources or fileSources
      // Note: We need to check against the unprefixed IDs since that's what the API returns
      const isDatabaseSource = freshDatabaseSources.some(source => source.id.endsWith('-' + id));
      const isFileSource = freshFileSources.some(source => source.id.endsWith('-' + id));
      
      if (isFileSource) {
        return `file-${id}`;
      } else if (isDatabaseSource) {
        return `db-${id}`;
      }
      // If not found in either, default to database source for backward compatibility
      return `db-${id}`;
    });
    
    // Set the initial selection and open the modal
    setInitialDataSourceSelection(dataSourceIds);
    setDataSourceSelectionModalOpen(true);
  };

  const handleSaveDataSources = async (selectedDataSourceIds: string[]) => {
    if (!selectedChatbotForDataSources) return;
    
    try {
      // Remove prefixes for API call
      const apiDataSourceIds = selectedDataSourceIds.map(id => {
        if (id.startsWith('db-')) {
          return id.substring(3); // Remove 'db-' prefix
        } else if (id.startsWith('file-')) {
          return id.substring(5); // Remove 'file-' prefix
        }
        return id; // No prefix
      });
      
      // Call the API to update the chatbot with the selected data sources
      // The API expects dataSourceIds (camelCase) instead of data_source_ids (snake_case)
      await updateChatbot(selectedChatbotForDataSources.id, {
        dataSourceIds: apiDataSourceIds
      });
      
      // Close the modal
      setDataSourceSelectionModalOpen(false);
      setSelectedChatbotForDataSources(null);
      setInitialDataSourceSelection([]);
      
      // Refresh chatbots to reflect the changes
      await fetchChatbots();
    } catch (err) {
      console.error('Failed to save data sources:', err);
      alert('Failed to save data sources. Please try again.');
    }
  };

  const handleCloseDataSourceSelectionModal = () => {
    setDataSourceSelectionModalOpen(false);
    setSelectedChatbotForDataSources(null);
    setInitialDataSourceSelection([]);
  };

  const stats = {
    activeChatbots: analyticsOverview?.kpis.activeChatbots ?? chatbots.filter((bot) => bot.status === 'published').length,
    connectedSources: analyticsOverview?.kpis.connectedSources ?? (databaseSources.length + fileSources.length),
    apiQueriesToday: analyticsOverview?.kpis.queriesToday ?? 0,
  };
  const previousKpis = analyticsOverview?.comparison?.previousKpis;
  const dashboardCardDeltas = {
    activeChatbots: null as number | null,
    connectedSources: null as number | null,
    apiQueriesToday: percentDelta(
      analyticsOverview?.kpis.queriesToday ?? 0,
      previousKpis?.queriesToday ?? 0
    ),
  };

  const trendPoints = analyticsOverview?.trends ?? [];
  const hasTrendData = trendPoints.some((point) => point.queries > 0 || point.tokens > 0 || point.sessions > 0);
  const dashboardTrendWindow = trendPoints.slice(-7);

  const interactionData = dashboardTrendWindow.map((point) => ({
    day: shortDateFormatter.format(new Date(point.day)),
    queries: point.queries,
    sessions: point.sessions,
  }));

  const sourceDistributionData = (analyticsOverview?.sourceDistribution ?? []).map((item) => ({
    name: item.name,
    value: item.value,
  }));
  const hasSourceDistributionData = sourceDistributionData.some((item) => item.value > 0);

  const performanceData = dashboardTrendWindow.map((point) => ({
    day: shortDateFormatter.format(new Date(point.day)),
    responseTimeMs: point.avgResponseTimeMs,
    tokens: point.tokens,
  }));

  const analyticsTrendData = trendPoints.map((point) => ({
    day: shortDateFormatter.format(new Date(point.day)),
    queries: point.queries,
    sessions: point.sessions,
    tokens: point.tokens,
    costUsd: point.costUsd,
    avgResponseTimeMs: point.avgResponseTimeMs,
  }));
  const hasTopChatbotsData = (analyticsOverview?.topChatbots ?? []).some((item) => item.queries > 0);

  const dashboardChartLayoutKey = `dashboard-${isMobileViewport ? 'm' : 'd'}-${sidebarExpanded ? 'open' : 'closed'}-${sidebarCollapsed ? 'collapsed' : 'expanded'}-${chartLayoutNonce}`;
  const analyticsChartLayoutKey = `analytics-${isMobileViewport ? 'm' : 'd'}-${chartLayoutNonce}`;

  const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981'];


  const additionalStats = [
    {
      label: `Queries (${selectedTimeRange}d)`,
      value: numberFormatter.format(analyticsOverview?.kpis.totalQueries ?? 0),
      icon: '📈',
      subtext: `Total user queries in the last ${selectedTimeRange} days`,
      delta: percentDelta(analyticsOverview?.kpis.totalQueries ?? 0, previousKpis?.totalQueries ?? 0),
    },
    {
      label: 'Unique Sessions',
      value: numberFormatter.format(analyticsOverview?.kpis.uniqueSessions ?? 0),
      icon: '👥',
      subtext: `Distinct chatbot sessions in the last ${selectedTimeRange} days`,
      delta: percentDelta(analyticsOverview?.kpis.uniqueSessions ?? 0, previousKpis?.uniqueSessions ?? 0),
    },
    {
      label: `Tokens (${selectedTimeRange}d)`,
      value: compactNumberFormatter.format(analyticsOverview?.kpis.totalTokens ?? 0),
      icon: '🔤',
      subtext: 'Total LLM token consumption',
      delta: percentDelta(analyticsOverview?.kpis.totalTokens ?? 0, previousKpis?.totalTokens ?? 0),
    },
  ];

  return (
    <div className={`dashboard-page ${activeTab === 'chatbots' && inPageChatbot ? 'in-page-chat-open' : ''}`}>
      <style>
        {`
          /* Align sidebar toggle with in-page chat header when in-page chat is open */
          .dashboard-page.in-page-chat-open .sidebar-toggle {
            top: 33px;
            transform: translateY(-50%);
          }

          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1.5rem;
          }

          .header-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: var(--text-primary);
          }

          .header-actions {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .header-theme-toggle {
            border-radius: 999px;
            border: 1px solid var(--border);
            background: var(--bg-tertiary);
            color: var(--text-secondary);
            padding: 6px 10px;
            font-size: 14px;
            cursor: pointer;
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            transition:
              background 0.18s ease,
              border-color 0.18s ease,
              transform 0.08s ease;
          }

          .header-theme-toggle:hover {
            border-color: var(--primary);
            background: var(--hover-bg);
          }

          .header-theme-toggle:active {
            transform: scale(0.96);
          }
          
          .in-page-chat-container {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(139, 92, 246, 0.03) 100%);
            border-radius: 16px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            height: 100%;
            border: 1px solid var(--border);
            box-shadow: var(--shadow-sm);
            backdrop-filter: blur(10px);
          }

          .light-theme .in-page-chat-container {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.04) 0%, rgba(139, 92, 246, 0.02) 100%);
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          }
          
          .in-page-chat-header {
            padding: 14px 18px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid var(--border);
            background: var(--bg-secondary);
            backdrop-filter: blur(12px);
          }

          .light-theme .in-page-chat-header {
            background: var(--card-bg);
            border-bottom: 1px solid var(--border);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          }

          .in-page-chat-header-row {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            flex: 1;
            min-width: 0;
          }
          
          .in-page-chat-header h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .in-page-chat-title {
            display: flex;
            flex-direction: column;
            gap: 10px;
            min-width: 0;
          }

          .in-page-chat-title-row {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;
          }

          .in-page-chat-avatar {
            width: 38px;
            height: 38px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
            flex-shrink: 0;
            font-size: 18px;
            color: #fff;
          }

          .light-theme .in-page-chat-avatar {
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
          }

          .in-page-chat-subtitle {
            margin-top: 2px;
            font-size: 12px;
            color: var(--text-secondary);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 56vw;
          }

          .in-page-chat-chips {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }

          .chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.2px;
            border: 1px solid rgba(59, 130, 246, 0.35);
            background: rgba(59, 130, 246, 0.1);
            color: var(--text-primary);
          }

          .light-theme .chip {
            background: rgba(59, 130, 246, 0.08);
            border-color: rgba(59, 130, 246, 0.3);
          }

          .chip-muted {
            border: 1px solid var(--border);
            background: var(--bg-tertiary);
            color: var(--text-secondary);
          }

          .light-theme .chip-muted {
            background: var(--hover-bg);
          }

          @media (max-width: 768px) {
            .in-page-chat-header-row {
              justify-content: flex-end;
              flex-wrap: nowrap;
            }

            .in-page-chat-title {
              align-items: flex-end;
              text-align: right;
            }

            .in-page-chat-title-row {
              justify-content: flex-end;
            }

            .in-page-chat-subtitle {
              display: none;
            }
          }
          
          .in-page-chat-header-actions {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .in-page-chat-header-actions button {
            background: var(--bg-tertiary);
            border: 1px solid var(--border);
            color: var(--text-primary);
            cursor: pointer;
            font-size: 18px;
            width: 36px;
            height: 36px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.18s ease;
          }

          .light-theme .in-page-chat-header-actions button {
            background: var(--hover-bg);
          }

          .in-page-chat-header-actions button:hover {
            background: rgba(59, 130, 246, 0.14);
            border-color: rgba(59, 130, 246, 0.35);
            transform: translateY(-1px);
          }

          .light-theme .in-page-chat-header-actions button:hover {
            background: rgba(59, 130, 246, 0.12);
          }

          .new-chat-btn {
            background: var(--bg-tertiary);
            border: 1px solid var(--border);
            color: var(--text-primary);
            cursor: pointer;
            font-size: 18px;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }

          .light-theme .new-chat-btn {
            background: var(--hover-bg);
          }

          .new-chat-btn:hover {
            background: rgba(59, 130, 246, 0.14);
            border-color: rgba(59, 130, 246, 0.35);
            transform: translateY(-1px);
          }

          .new-chat-btn:focus-visible {
            outline: 2px solid var(--primary);
            outline-offset: 2px;
          }

          .light-theme .new-chat-btn:hover {
            background: rgba(59, 130, 246, 0.12);
          }

          .close-in-page-chat {
            background: var(--bg-tertiary);
            border: 1px solid var(--border);
            color: var(--text-primary);
            cursor: pointer;
            font-size: 24px;
            width: 40px;
            height: 40px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }

          .light-theme .close-in-page-chat {
            background: var(--hover-bg);
          }

          .close-in-page-chat:hover {
            background: rgba(239, 68, 68, 0.12);
            border-color: rgba(239, 68, 68, 0.35);
            color: var(--danger);
            transform: translateY(-1px);
          }

          .close-in-page-chat:focus-visible {
            outline: 2px solid var(--danger);
            outline-offset: 2px;
          }

          .light-theme .close-in-page-chat:hover {
            background: rgba(239, 68, 68, 0.1);
          }
          
          .in-page-chat-content {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          
          /* Make the in-page chat window take full height */
          .inPageChat {
            position: relative;
            width: 100%;
            height: 100%;
            border-radius: 0;
            box-shadow: none;
            animation: none;
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          
          /* Adjust chat messages container for in-page chat */
          .inPageChat .chatMessages {
            flex: 1;
            max-height: none;
          }
          
          /* Ensure proper spacing and alignment */
          .in-page-chat-content .chatWindow {
            flex: 1;
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
            max-width: none;
            border-radius: 0;
            box-shadow: none;
            position: static;
          }
          
          /* Ensure chat window takes full width of in-page chat container */
          .in-page-chat-container .chatWindow {
            width: 100%;
          }
          
          .in-page-chat-content .chatMessages {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
          }
          
          .in-page-chat-content .chatInputArea {
            padding: 16px 20px;
            background: var(--card-bg);
            border-top: 1px solid var(--border);
          }
          
          /* Mobile responsive styles for in-page chat - full viewport like ChatGPT */
          @media (max-width: 768px) {
            .header {
              margin-bottom: 0.75rem;
            }

            .in-page-chat-container {
              position: fixed;
              inset: 0;
              width: 100%;
              height: 100%;
              height: 100vh;
              height: 100svh;
              max-height: -webkit-fill-available;
              z-index: 50;
              border-radius: 0;
              border: none;
              box-shadow: none;
              display: flex;
              flex-direction: column;
              background: var(--bg-primary);
            }
            
            .in-page-chat-header {
              padding: 10px;
              border-bottom: none;
              flex-shrink: 0;
            }

            .light-theme .in-page-chat-header {
              border-bottom: 1px solid var(--border);
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
            }

            .in-page-chat-header h3 {
              font-size: 16px;
            }
            
            .in-page-chat-header-actions button,
            .new-chat-btn,
            .close-in-page-chat {
              border: none;
            }
            
            .close-in-page-chat {
              width: 36px;
              height: 36px;
              font-size: 20px;
            }

            .new-chat-btn {
              width: 36px;
              height: 36px;
              font-size: 20px;
            }
            
            .in-page-chat-content {
              flex: 1;
              min-height: 0;
              width: 100%;
              height: 100%;
            }
            
            .in-page-chat-content .chatMessages {
              padding: 16px;
            }
            
            .in-page-chat-content .chatInputArea {
              padding: 16px;
              border-top: none;
            }
            
            .dashboard-page.in-page-chat-open .sidebar-toggle {
              top: 29px;
              transform: translateY(-50%);
            }
          }
          
          @media (max-width: 480px) {
            .header {
              margin-bottom: 0.5rem;
            }

            .in-page-chat-header {
              padding: 8px;
            }
            
            .dashboard-page.in-page-chat-open .sidebar-toggle {
              top: 27px;
              transform: translateY(-50%);
            }
            
            .in-page-chat-header h3 {
              font-size: 15px;
            }
            
            .close-in-page-chat {
              width: 32px;
              height: 32px;
              font-size: 18px;
            }

            .new-chat-btn {
              width: 32px;
              height: 32px;
              font-size: 18px;
            }
            
            .in-page-chat-content .chatMessages {
              padding: 14px;
            }
            
            .in-page-chat-content .chatInputArea {
              padding: 14px;
            }
          }
          
          /* Mobile responsive styles for sidebar footer */
          @media (max-width: 768px) {
            .sidebar-footer-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
            }
            
            .sidebar-footer .user-profile {
              justify-content: center;
              width: 100%;
            }
            
            .sidebar-footer .sidebar-footer-actions {
              display: flex;
              justify-content: center;
              margin-top: 10px;
              width: 100%;
            }
            
            .sidebar-footer .logout-btn {
              position: relative;
              top: 0;
              right: 0;
              margin-left: 10px;
            }
            
            .user-info {
              flex-grow: 1;
              text-align: center;
            }
          }
          
          /* History Modal Styles */
          .history-modal-content {
            max-height: 70vh;
            overflow-y: auto;
          }
          
          .session-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .session-content {
            flex-grow: 1;
          }
          
          .delete-session-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 1.2rem;
            padding: 0.5rem;
            border-radius: 4px;
            transition: background-color 0.2s ease;
          }
          
          .delete-session-btn:hover {
            background-color: rgba(255, 0, 0, 0.1);
          }
          
          .chat-history-list h3 {
            margin-top: 0;
            margin-bottom: 1rem;
            color: var(--text-primary);
          }
          
          .session-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .session-item {
            padding: 1rem;
            border: 1px solid var(--border);
            border-radius: 8px;
            background-color: var(--card-bg);
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .session-item:hover {
            background-color: var(--primary);
            color: white;
          }
          
          .session-message {
            font-weight: 500;
            margin-bottom: 0.5rem;
          }
          
          .session-timestamp {
            font-size: 0.8rem;
            color: var(--text-secondary);
          }
          
          .session-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1rem;
          }
          
          .message-history {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          
          .message-pair {
            padding: 1rem;
            border: 1px solid var(--border);
            border-radius: 8px;
            background-color: var(--card-bg);
          }
          
          .user-message, .bot-message {
            margin-bottom: 0.5rem;
          }
          
          .message-tokens, .message-time, .message-timestamp {
            font-size: 0.8rem;
            color: var(--text-secondary);
            margin-bottom: 0.25rem;
          }
          
          .message-timestamp {
            margin-top: 0.5rem;
            padding-top: 0.5rem;
            border-top: 1px solid var(--border);
          }
        `}
      </style>
      <button className="sidebar-toggle" onClick={toggleSidebar}>☰</button>
      {sidebarExpanded && <div className="mobile-overlay" onClick={closeSidebar}></div>}
      <div className="container">
        <aside className={`sidebar ${sidebarExpanded ? 'expanded' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <button type="button" className="logo" onClick={() => showTab('dashboard')}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/arihex-logo-${theme === 'dark' ? 'dark' : 'light'}.png`}
                alt="Arihex"
                className="logo-img"
              />
            </button>
            <div className="sidebar-collapse-btn" onClick={toggleSidebarCollapse}>
              <span className="collapse-icon">›</span>
            </div>
          </div>
          <nav className="nav-section">
            <div className="nav-section-title"></div>
            <button className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => showTab('dashboard')}>
              <span className="nav-icon">📊</span>
              <span className="nav-item-label">Dashboard</span>
            </button>
            <button className={`nav-item ${activeTab === 'chatbots' ? 'active' : ''}`} onClick={() => showTab('chatbots')}>
              <span className="nav-icon">💬</span>
              <span className="nav-item-label">Agents</span>
            </button>
            <button className={`nav-item ${activeTab === 'sources' ? 'active' : ''}`} onClick={() => showTab('sources')}>
              <span className="nav-icon">📁</span>
              <span className="nav-item-label">Data Sources</span>
            </button>
            <button className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => showTab('analytics')}>
              <span className="nav-icon">📈</span>
              <span className="nav-item-label">Analytics</span>
            </button>
            <button className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => showTab('settings')}>
              <span className="nav-icon">⚙️</span>
              <span className="nav-item-label">Settings</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <div className="sidebar-footer-content">
              <div className="user-profile">
                <div className="user-avatar">
                  {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  {user?.lastName?.charAt(0) || user?.email?.split('@')[0]?.charAt(1) || 'U'}
                </div>
                <div className="user-info">
                  <div className="user-name">{user?.firstName} {user?.lastName}</div>
                  <div className="user-email">{user?.email}</div>
                </div>
                <button className="logout-btn" title="Logout" onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 17L21 12M21 12L16 7M21 12H9M9 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {/* <div className="sidebar-footer-actions">
                <button className="btn btn-secondary btn-sm" onClick={toggleTheme} title="Toggle Theme">
                  <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
                </button>
              </div> */}
            </div>
          </div>
        </aside>
        <div className="main-content">
          <div className={`header ${activeTab === 'chatbots' && inPageChatbot ? 'hidden' : ''}`}>
            <h1 className="header-title">
              {isStaticPage
                ? staticPageTitle
                : activeTab === 'chatbots'
                  ? 'Agents'
                  : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h1>
            <div className="header-actions">
              {!isStaticPage && (activeTab === 'dashboard' || activeTab === 'analytics') && (
                
                <div className="range-control-wrap">
                  
                  <div className="range-control" role="group" aria-label="Analytics time range">
                    {TIME_RANGE_OPTIONS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`range-btn ${selectedTimeRange === option ? 'active' : ''}`}
                        onClick={() => setSelectedTimeRange(option)}
                      >
                        {option}d
                      </button>
                    ))}
                  </div>
                  
                </div>
              )}
              <button
                type="button"
                className="header-theme-toggle"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                title="Toggle theme"
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
          <div ref={mainContentRef} className={`content-area ${activeTab === 'analytics' ? 'analytics-view' : ''} ${activeTab === 'chatbots' ? 'chatbots-view' : ''} ${activeTab === 'sources' ? 'sources-view' : ''} ${isStaticPage ? 'static-page-view' : ''}`}>
            {isStaticPage && (
              <>
                {isTermsPage && <TermsOfServicePage />}
                {isPrivacyPage && <PrivacyPolicyPage />}
                {isContactPage && <ContactSupportPage />}
                {isAboutPage && <AboutPage />}
              </>
            )}
            {!isStaticPage && activeTab === 'dashboard' && (
              <>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">
                      <span className="stat-icon">💬</span>
                      <span>Active Agents</span>
                    </div>
                    <div className="stat-value">{stats.activeChatbots}</div>
                    <div className="kpi-delta-chip neutral">Snapshot</div>
                    <div className="stat-subtext">Published agents currently serving traffic</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">
                      <span className="stat-icon">📁</span>
                      <span>Connected Sources</span>
                    </div>
                    <div className="stat-value">{stats.connectedSources}</div>
                    <div className="kpi-delta-chip neutral">Snapshot</div>
                    <div className="stat-subtext">Active database and file knowledge sources</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">
                      <span className="stat-icon">💡</span>
                      <span>API Queries Today</span>
                    </div>
                    <div className="stat-value">{numberFormatter.format(stats.apiQueriesToday)}</div>
                    <div className={`kpi-delta-chip ${
                      (dashboardCardDeltas.apiQueriesToday ?? 0) > 0 ? 'positive' : (dashboardCardDeltas.apiQueriesToday ?? 0) < 0 ? 'negative' : 'neutral'
                    }`}>
                      {formatDeltaChip(dashboardCardDeltas.apiQueriesToday)} vs previous period
                    </div>
                    <div className="stat-subtext">Queries processed since today 00:00</div>
                  </div>
                </div>
                {analyticsLoading && !analyticsOverview && (
                  <div className="dashboard-state-grid">
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                  </div>
                )}
                {!analyticsLoading && analyticsError && (
                  <div className="chart-card state-card">
                    <h3 className="chart-title">Unable to load dashboard insights</h3>
                    <p className="chart-subtitle">{analyticsError}</p>
                    <button className="btn btn-primary btn-sm" onClick={fetchAnalyticsOverview}>
                      Retry
                    </button>
                  </div>
                )}
                {!analyticsLoading && !analyticsError && analyticsOverview && !hasTrendData && (
                  <div className="chart-card state-card">
                    <h3 className="chart-title">No dashboard activity yet</h3>
                    <p className="chart-subtitle">Once users start querying your agents, charts and trends will appear here.</p>
                  </div>
                )}
                
                {/* Chart Section */}
                {!analyticsError && hasTrendData && (
                <div className="chart-grid dashboard-chart-grid" key={dashboardChartLayoutKey}>
                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Query Volume & Sessions</h3>
                      <p className="chart-subtitle">Last 7 days activity</p>
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={isMobileViewport ? 220 : 300}>
                        <BarChart data={interactionData} margin={isMobileViewport ? { top: 8, right: 8, left: -18, bottom: 4 } : undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="day" stroke="var(--text-secondary)" minTickGap={isMobileViewport ? 14 : 24} tick={{ fontSize: isMobileViewport ? 10 : 12 }} />
                          <YAxis stroke="var(--text-secondary)" tick={{ fontSize: isMobileViewport ? 10 : 12 }} width={isMobileViewport ? 28 : 40} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--card-bg)', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }} 
                          />
                          {!isMobileViewport && <Legend />}
                          <Bar dataKey="queries" name="Queries" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="sessions" name="Sessions" fill="var(--secondary)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Source Distribution</h3>
                      <p className="chart-subtitle">Data sources breakdown</p>
                    </div>
                    <div className="chart-container">
                      {!hasSourceDistributionData ? (
                        <div className="empty-chart-state">No source data available</div>
                      ) : (
                      <ResponsiveContainer width="100%" height={isMobileViewport ? 220 : 300}>
                        <PieChart>
                          <Pie
                            data={sourceDistributionData}
                            cx="50%"
                            cy="50%"
                            labelLine={!isMobileViewport}
                            outerRadius={isMobileViewport ? 70 : 100}
                            fill="#8884d8"
                            dataKey="value"
                            label={isMobileViewport ? false : ({ name, percent }) => `${name}: ${Math.round((percent ?? 0) * 100)}%`}
                          >
                            {sourceDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--card-bg)', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }} 
                          />
                          <Legend
                            verticalAlign="bottom"
                            iconSize={isMobileViewport ? 8 : 12}
                            height={isMobileViewport ? 28 : undefined}
                            wrapperStyle={isMobileViewport ? { fontSize: '10px', paddingTop: '6px' } : undefined}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Latency & Token Trend</h3>
                      <p className="chart-subtitle">Response speed and token load</p>
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={isMobileViewport ? 220 : 300}>
                        <AreaChart data={performanceData} margin={isMobileViewport ? { top: 8, right: 8, left: -18, bottom: 4 } : undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="day" stroke="var(--text-secondary)" minTickGap={isMobileViewport ? 14 : 24} tick={{ fontSize: isMobileViewport ? 10 : 12 }} />
                          <YAxis stroke="var(--text-secondary)" tick={{ fontSize: isMobileViewport ? 10 : 12 }} width={isMobileViewport ? 28 : 40} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--card-bg)', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }} 
                          />
                          {!isMobileViewport && <Legend />}
                          <Area 
                            type="monotone" 
                            dataKey="responseTimeMs" 
                            name="Response Time (ms)" 
                            stroke="var(--primary)" 
                            fill="var(--primary)" 
                            fillOpacity={0.2} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="tokens" 
                            name="Tokens" 
                            stroke="var(--secondary)" 
                            strokeWidth={2}
                            dot={{ r: isMobileViewport ? 2 : 4 }}
                            activeDot={{ r: isMobileViewport ? 4 : 6 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                )}
              </>
            )}
            {!isStaticPage && activeTab === 'chatbots' && (
              inPageChatbot ? (
                <div className="in-page-chat-container">
                  <div className="in-page-chat-header">
                    <div className="in-page-chat-header-row">
                      <div className="in-page-chat-title">
                        <div className="in-page-chat-title-row">
                          <div className="in-page-chat-avatar">🤖</div>
                          <div>
                            <h3>{inPageChatbot.name}</h3>
                            <div className="in-page-chat-subtitle">
                              {inPageChatbot.description || 'RAG assistant • grounded in your connected data'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="in-page-chat-header-actions">
                        <button
                          className="new-chat-btn"
                          onClick={() => {
                            inPageChatRef.current?.startNewChat();
                            setSessionHistory([]);
                            setSelectedSession(null);
                          }}
                          title={`New chat (${newChatShortcutLabel})`}
                          aria-label={`New chat (${newChatShortcutLabel})`}
                        >
                          ✨
                        </button>
                        <button
                          className="close-in-page-chat"
                          onClick={() => setInPageChatbot(null)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="in-page-chat-content">
                    <InPageChat 
                      ref={inPageChatRef}
                      chatbot={inPageChatbot} 
                      preloadedMessages={sessionHistory}
                      preloadedSessionId={selectedSession}
                      onOpenChange={(isOpen) => setInPageChatOpen(isOpen)}
                    />
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-header">
                    <div>
                      <div className="card-title">Your Agents</div>
                      <div className="card-subtitle">Manage all your active AI agent instances</div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => openCreateModal('chatbot')}>+ New Agent</button>
                  </div>
                  <div className="chatbot-content">
                    {loading && <div className="loading">Loading agents...</div>}
                    {!loading && chatbots.map((chatbot) => (
                      <div
                        key={chatbot.id}
                        className="list-item list-item-clickable"
                        onClick={() => setInPageChatbot(chatbot)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setInPageChatbot(chatbot);
                          }
                        }}
                      >
                        <div>
                          <div className="list-item-name">{chatbot.name}</div>
                          <div className="list-item-type">
                            {chatbot.description} • {chatbot.status || 'draft'}
                            {chatbot.created_at && (
                              <span> • Created: {new Date(chatbot.created_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                          <button className="card-action-btn" onClick={() => setInPageChatbot(chatbot)} title="Open chat">💬</button>
                          <button className="card-action-btn" onClick={() => fetchChatHistory(chatbot.id)} title="Chat history">📚</button>
                          <button className="card-action-btn" onClick={() => handleAttachDataSources(chatbot)} title="Attach data sources">🔗</button>
                          <button className="card-action-btn" onClick={() => handleEditChatbot(chatbot)} title="Edit">✏️</button>
                          <button className="card-action-btn" onClick={() => handleDeleteChatbot(chatbot.id)} title="Delete">🗑️</button>
                        </div>
                      </div>
                    ))}
                    {!loading && chatbots.length === 0 && (
                      <div className="empty-state">
                        <p>No agents created yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
            {!isStaticPage && activeTab === 'sources' && (
              <div className="card">
                <div className="card-header">
                  <div>
                    <div className="card-title">Data Sources</div>
                    <div className="card-subtitle">Manage your knowledge base connections</div>
                  </div>
                  <div className="card-action">
                    <button className="btn btn-primary btn-sm" onClick={() => openCreateModal('database')}>+ Database</button>
                    <button className="btn btn-primary btn-sm" onClick={() => openCreateModal('files')}>+ Files</button>
                  </div>
                </div>
                {error && <div className="error-message">{error}</div>}
                <div className="tabs">
                  <button className={`tab-btn ${activeSourceTab === 'databases' ? 'active' : ''}`} onClick={() => switchSourceTab('databases')}>
                    Databases
                  </button>
                  <button className={`tab-btn ${activeSourceTab === 'files' ? 'active' : ''}`} onClick={() => switchSourceTab('files')}>
                    Files
                  </button>
                </div>
                <div id="databases" className={`tab-content ${activeSourceTab === 'databases' ? 'active' : ''}`}>
                  {loading && <div className="loading">Loading databases...</div>}
                  {!loading && databaseSources.map((source: DataSource) => (
                    <div key={source.id} className="list-item">
                      <div>
                        <div className="list-item-name">{source.name}</div>
                        <div className="list-item-type">
                          {source.type} • {source.status || 'active'}
                          {source.created_at && (
                            <span> • Created: {new Date(source.created_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <button className="card-action-btn" onClick={() => handleDeleteDataSource(source.id)}>🗑️</button>
                    </div>
                  ))}
                  {!loading && databaseSources.length === 0 && (
                    <div className="empty-state">
                      <p>No databases connected yet.</p>
                    </div>
                  )}
                </div>
                <div id="files" className={`tab-content ${activeSourceTab === 'files' ? 'active' : ''}`}>
                  {uploadProgress !== null && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="progress-text">
                  Uploading... {uploadProgress}%
                </div>
              </div>
                  )}
                  {loading && !uploadProgress && <div className="loading">Loading files...</div>}
                  {!loading && fileSources.map((file: DataSource) => (
                    <div key={file.id} className="list-item">
                      <div>
                        <div className="list-item-name">{file.name}</div>
                        <div className="list-item-type">
                          {(file.file_type || '').toUpperCase()} • {file.file_size || 0} bytes • {file.status || 'active'}
                          {file.created_at && (
                            <span> • Created: {new Date(file.created_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <button className="card-action-btn" onClick={() => handleDeleteDataSource(file.id, true)}>🗑️</button>
                    </div>
                  ))}
                  {!loading && fileSources.length === 0 && (
                    <div className="empty-state">
                      <p>No files uploaded yet.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!isStaticPage && activeTab === 'analytics' && (
              <div className="analytics-content">
                <div className="stats-grid">
                  {additionalStats.map((stat, index) => (
                    <div className="stat-card" key={index}>
                      <div className="stat-label">
                        <span className="stat-icon">{stat.icon}</span>
                        <span>{stat.label}</span>
                      </div>
                      <div className="stat-value">{stat.value}</div>
                      <div className={`kpi-delta-chip ${
                        (stat.delta ?? 0) > 0 ? 'positive' : (stat.delta ?? 0) < 0 ? 'negative' : 'neutral'
                      }`}>
                        {formatDeltaChip(stat.delta)} vs previous period
                      </div>
                      <div className="stat-subtext">{stat.subtext}</div>
                    </div>
                  ))}
                </div>
                {analyticsLoading && !analyticsOverview && (
                  <div className="dashboard-state-grid">
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                  </div>
                )}
                {!analyticsLoading && analyticsError && (
                  <div className="chart-card state-card">
                    <h3 className="chart-title">Unable to load analytics</h3>
                    <p className="chart-subtitle">{analyticsError}</p>
                    <button className="btn btn-primary btn-sm" onClick={fetchAnalyticsOverview}>
                      Retry
                    </button>
                  </div>
                )}
                {!analyticsLoading && !analyticsError && analyticsOverview && !hasTrendData && (
                  <div className="chart-card state-card">
                    <h3 className="chart-title">No analytics data for this period</h3>
                    <p className="chart-subtitle">Try expanding the date range or start sending queries to your agents.</p>
                  </div>
                )}
                
                {/* Analytics Charts */}
                {!analyticsError && hasTrendData && (
                <div className="chart-grid analytics-chart-grid" key={analyticsChartLayoutKey}>
                  
                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Query Trend</h3>
                      <p className="chart-subtitle">Daily query and session volume ({selectedTimeRange} days)</p>
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={isMobileViewport ? 240 : 350}>
                        <LineChart data={analyticsTrendData} margin={isMobileViewport ? { top: 8, right: 8, left: -18, bottom: 4 } : undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="day" stroke="var(--text-secondary)" minTickGap={isMobileViewport ? 14 : 24} tick={{ fontSize: isMobileViewport ? 10 : 12 }} />
                          <YAxis stroke="var(--text-secondary)" tick={{ fontSize: isMobileViewport ? 10 : 12 }} width={isMobileViewport ? 28 : 40} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--card-bg)', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }} 
                          />
                          {!isMobileViewport && <Legend />}
                          <Line
                            type="monotone"
                            dataKey="queries"
                            name="Queries"
                            stroke="var(--primary)"
                            strokeWidth={3}
                            dot={{ r: isMobileViewport ? 2 : 3 }}
                            activeDot={{ r: isMobileViewport ? 4 : 6 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="sessions"
                            name="Sessions"
                            stroke="var(--secondary)"
                            strokeWidth={2}
                            dot={{ r: isMobileViewport ? 1.5 : 2 }}
                            activeDot={{ r: isMobileViewport ? 3 : 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Token Usage Trend</h3>
                      <p className="chart-subtitle">Daily tokens consumed and estimated cost ({selectedTimeRange} days)</p>
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={isMobileViewport ? 240 : 350}>
                        <LineChart data={analyticsTrendData} margin={isMobileViewport ? { top: 8, right: 8, left: -18, bottom: 4 } : undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="day" stroke="var(--text-secondary)" minTickGap={isMobileViewport ? 14 : 24} tick={{ fontSize: isMobileViewport ? 10 : 12 }} />
                          <YAxis stroke="var(--text-secondary)" tick={{ fontSize: isMobileViewport ? 10 : 12 }} width={isMobileViewport ? 28 : 40} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--card-bg)', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }} 
                          />
                          {!isMobileViewport && <Legend />}
                          <Line 
                            type="monotone" 
                            dataKey="tokens" 
                            name="Tokens" 
                            stroke="var(--primary)" 
                            strokeWidth={2}
                            dot={{ r: isMobileViewport ? 2 : 4 }}
                            activeDot={{ r: isMobileViewport ? 4 : 6 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="costUsd" 
                            name="Cost (USD)" 
                            stroke="var(--secondary)" 
                            strokeWidth={2}
                            dot={{ r: isMobileViewport ? 2 : 3 }}
                            activeDot={{ r: isMobileViewport ? 4 : 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="chart-card">
                    <div className="chart-header">
                      <h3 className="chart-title">Top Agents by Queries</h3>
                      <p className="chart-subtitle">Last {selectedTimeRange} days</p>
                    </div>
                    <div className="chart-container">
                      {!hasTopChatbotsData ? (
                        <div className="empty-chart-state">No query activity for this period</div>
                      ) : (
                      <ResponsiveContainer width="100%" height={isMobileViewport ? 240 : 320}>
                        <BarChart data={analyticsOverview?.topChatbots ?? []} margin={isMobileViewport ? { top: 8, right: 8, left: -18, bottom: 16 } : undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis
                            dataKey="name"
                            stroke="var(--text-secondary)"
                            tick={{ fontSize: isMobileViewport ? 10 : 12 }}
                            interval={0}
                            minTickGap={isMobileViewport ? 8 : 20}
                            tickFormatter={(value: string) => truncateLabel(value, isMobileViewport ? 8 : 20)}
                          />
                          <YAxis stroke="var(--text-secondary)" tick={{ fontSize: isMobileViewport ? 10 : 12 }} width={isMobileViewport ? 28 : 40} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--card-bg)', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }} 
                          />
                          <Bar dataKey="queries" name="Queries" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>
            )}
            {!isStaticPage && activeTab === 'settings' && (
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Account Settings</div>
                </div>
                <div className="tabs settings-tabs">
                  <button
                    className={`tab-btn ${activeSettingsTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveSettingsTab('profile')}
                  >
                    Profile
                  </button>
                  <button
                    className={`tab-btn ${activeSettingsTab === 'security' ? 'active' : ''}`}
                    onClick={() => setActiveSettingsTab('security')}
                  >
                    Security
                  </button>
                </div>

                {activeSettingsTab === 'profile' && (
                  <form
                    className="settings-section"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setProfileFormError(null);
                      setProfileFormSuccess(null);
                      setProfileCompanyTouched(true);

                      const err = validateRequiredField(companyName, 'Company name', 2);
                      if (err) {
                        setProfileFormError(err);
                        return;
                      }

                      const trimmed = companyName.trim();
                      try {
                        const res = await updateCompanyName({ companyName: trimmed });
                        setProfileFormSuccess(res.message || 'Company name updated successfully.');
                      } catch (err: any) {
                        setProfileFormError(err?.message || 'Failed to update company name');
                      }
                    }}
                  >
                    {profileFormError && (
                      <div className="app-error">
                        {profileFormError}
                      </div>
                    )}
                    {profileFormSuccess && (
                      <div className="app-success">
                        {profileFormSuccess}
                      </div>
                    )}
                    <div className={modalStyles.formGroup}>
                      <label className={modalStyles.formLabel} htmlFor="settings-email">Email Address</label>
                      <input
                        type="email"
                        id="settings-email"
                        className={modalStyles.formInput}
                        value={user?.email || ''}
                        disabled
                      />
                    </div>
                    <div
                      className={`${modalStyles.formGroup} ${
                        profileCompanyError
                          ? modalStyles.formGroupInvalid
                          : profileCompanyTouched && !profileCompanyError
                          ? modalStyles.formGroupValid
                          : ''
                      }`}
                    >
                      <label className={modalStyles.formLabel} htmlFor="settings-companyName">Company Name</label>
                      <input
                        type="text"
                        id="settings-companyName"
                        name="companyName"
                        className={modalStyles.formInput}
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        onBlur={() => setProfileCompanyTouched(true)}
                        aria-invalid={!!profileCompanyError}
                        aria-describedby={profileCompanyError ? 'settings-companyName-err' : undefined}
                      />
                      {profileCompanyError && (
                        <span
                          id="settings-companyName-err"
                          className={modalStyles.fieldError}
                          role="alert"
                        >
                          {profileCompanyError}
                        </span>
                      )}
                    </div>
                    <div className={modalStyles.formActions}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!profileFormValid}
                      >
                        Save Profile
                      </button>
                    </div>
                  </form>
                )}

                {activeSettingsTab === 'security' && (
                  <form
                    className="settings-section"
                    onSubmit={async (e) => {
                      e.preventDefault();

                      setPasswordFormError(null);
                      setPasswordFormSuccess(null);

                      // mark all fields as touched so errors are shown
                      setSecurityTouched({ current: true, new: true, confirm: true });

                      if (!securityFormValid) {
                        return;
                      }

                      try {
                        const res = await updatePassword({
                          currentPassword: securityCurrentPassword,
                          newPassword: securityNewPassword,
                        });
                        setPasswordFormSuccess(res.message || 'Password updated successfully.');
                        setSecurityCurrentPassword('');
                        setSecurityNewPassword('');
                        setSecurityConfirmPassword('');
                        setSecurityTouched({ current: false, new: false, confirm: false });
                      } catch (err: any) {
                        setPasswordFormError(
                          err?.message ||
                            'Failed to update password. Please check your current password and try again.'
                        );
                      }
                    }}
                  >
                    {passwordFormError && (
                      <div className="app-error">
                        {passwordFormError}
                      </div>
                    )}
                    {passwordFormSuccess && (
                      <div className="app-success">
                        {passwordFormSuccess}
                      </div>
                    )}
                    <div
                      className={`${modalStyles.formGroup} ${
                        securityCurrentError
                          ? modalStyles.formGroupInvalid
                          : securityTouched.current && !securityCurrentError
                          ? modalStyles.formGroupValid
                          : ''
                      }`}
                    >
                      <label className={modalStyles.formLabel} htmlFor="settings-currentPassword">Current Password</label>
                      <input
                        type="password"
                        id="settings-currentPassword"
                        name="currentPassword"
                        className={modalStyles.formInput}
                        placeholder="••••••••"
                        value={securityCurrentPassword}
                        onChange={(e) => setSecurityCurrentPassword(e.target.value)}
                        onBlur={() => setSecurityTouched((t) => ({ ...t, current: true }))}
                        aria-invalid={!!securityCurrentError}
                        aria-describedby={securityCurrentError ? 'settings-currentPassword-err' : undefined}
                      />
                    </div>
                    {securityCurrentError && (
                      <span
                        id="settings-currentPassword-err"
                        className={modalStyles.fieldError}
                        role="alert"
                      >
                        {securityCurrentError}
                      </span>
                    )}
                    <div
                      className={`${modalStyles.formGroup} ${
                        securityNewError
                          ? modalStyles.formGroupInvalid
                          : securityTouched.new && !securityNewError
                          ? modalStyles.formGroupValid
                          : ''
                      }`}
                    >
                      <label className={modalStyles.formLabel} htmlFor="settings-newPassword">New Password</label>
                      <input
                        type="password"
                        id="settings-newPassword"
                        name="newPassword"
                        className={modalStyles.formInput}
                        placeholder="••••••••"
                        value={securityNewPassword}
                        onChange={(e) => setSecurityNewPassword(e.target.value)}
                        onBlur={() => setSecurityTouched((t) => ({ ...t, new: true }))}
                        aria-invalid={!!securityNewError}
                        aria-describedby={securityNewError ? 'settings-newPassword-err' : undefined}
                      />
                    </div>
                    {securityNewError && (
                      <span
                        id="settings-newPassword-err"
                        className={modalStyles.fieldError}
                        role="alert"
                      >
                        {securityNewError}
                      </span>
                    )}
                    <div
                      className={`${modalStyles.formGroup} ${
                        securityConfirmError
                          ? modalStyles.formGroupInvalid
                          : securityTouched.confirm && !securityConfirmError
                          ? modalStyles.formGroupValid
                          : ''
                      }`}
                    >
                      <label className={modalStyles.formLabel} htmlFor="settings-confirmPassword">Confirm New Password</label>
                      <input
                        type="password"
                        id="settings-confirmPassword"
                        name="confirmPassword"
                        className={modalStyles.formInput}
                        placeholder="••••••••"
                        value={securityConfirmPassword}
                        onChange={(e) => setSecurityConfirmPassword(e.target.value)}
                        onBlur={() => setSecurityTouched((t) => ({ ...t, confirm: true }))}
                        aria-invalid={!!securityConfirmError}
                        aria-describedby={securityConfirmError ? 'settings-confirmPassword-err' : undefined}
                      />
                    </div>
                    {securityConfirmError && (
                      <span
                        id="settings-confirmPassword-err"
                        className={modalStyles.fieldError}
                        role="alert"
                      >
                        {securityConfirmError}
                      </span>
                    )}
                    <div className={modalStyles.formActions}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!securityFormValid}
                      >
                        Update Password
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
          <footer className={`main-footer ${activeTab === 'chatbots' && inPageChatbot ? 'hidden' : ''}`}>
            <div className="footer-content">
              <p>© 2026 arihex.com All rights reserved.</p>
              <div className="footer-links">
                <Link to="/dashboard/about">About</Link>
                <Link to="/dashboard/terms">Terms of Service</Link>
                <Link to="/dashboard/privacy">Privacy Policy</Link>
                <Link to="/dashboard/contact">Contact Support</Link>
              </div>
            </div>
          </footer>
        </div>
      </div>
      {/* Modals */}
      <Modal isOpen={createModalOpen} onClose={closeCreateModal} title="Create New Agent">
        <div className={modalStyles.formGroup}>
          <label className={modalStyles.formLabel}>Agent Name</label>
          <input type="text" className={modalStyles.formInput} placeholder="e.g., Customer Support Agent" />
        </div>
        <div className={modalStyles.formGroup}>
          <label className={modalStyles.formLabel}>Description</label>
          <textarea className={`${modalStyles.formTextarea} ${modalStyles.compactTextarea}`} placeholder="Describe what this chatbot will do..."></textarea>
        </div>
        <div className={modalStyles.formGroup}>
          <label className={modalStyles.formLabel}>Industry Type</label>
          <select className={modalStyles.formSelect}>
            <option>Select industry</option>
            <option>E-Commerce</option>
            <option>Healthcare</option>
            <option>Finance</option>
            <option>Technology</option>
            <option>Education</option>
            <option>Tender</option>
          </select>
        </div>
        <div className={modalStyles.formActions}>
          <button className="btn btn-primary" onClick={closeCreateModal}>Create Agent</button>
          <button className="btn btn-secondary" onClick={closeCreateModal}>Cancel</button>
        </div>
      </Modal>
      <Modal isOpen={databaseModalOpen} onClose={closeCreateModal} title="Connect Database">
        <form onSubmit={handleCreateDataSource}>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Source Name</label>
            <input 
              type="text" 
              className={modalStyles.formInput} 
              placeholder="e.g., Production Database" 
              name="name"
              value={newDataSource.name}
              onChange={handleDataSourceChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Database Type</label>
            <select 
              className={modalStyles.formSelect}
              name="type"
              value={newDataSource.type}
              onChange={handleDataSourceChange}
            >
              <option value="postgresql">PostgreSQL</option>
              <option value="mysql">MySQL</option>
              <option value="mongodb">MongoDB</option>
              <option value="sqlserver">SQL Server</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Host</label>
            <input 
              type="text" 
              className={modalStyles.formInput} 
              placeholder="e.g., localhost" 
              name="host"
              value={newDataSource.host}
              onChange={handleDataSourceChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Port</label>
            <input 
              type="number" 
              className={modalStyles.formInput} 
              placeholder="e.g., 5432" 
              name="port"
              value={newDataSource.port}
              onChange={handleDataSourceChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Database Name</label>
            <input 
              type="text" 
              className={modalStyles.formInput} 
              placeholder="e.g., my_database" 
              name="database"
              value={newDataSource.database}
              onChange={handleDataSourceChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Username</label>
            <input 
              type="text" 
              className={modalStyles.formInput} 
              placeholder="e.g., db_user" 
              name="user"
              value={newDataSource.user}
              onChange={handleDataSourceChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Password</label>
            <input 
              type="password" 
              className={modalStyles.formInput} 
              placeholder="••••••••" 
              name="password"
              value={newDataSource.password}
              onChange={handleDataSourceChange}
              required
            />
          </div>
          <div className={modalStyles.formActions}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Connecting...' : 'Test & Connect'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeCreateModal} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
      <Modal isOpen={fileUploadModalOpen} onClose={closeCreateModal} title="Upload Files">
        <form onSubmit={handleFileUpload}>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Select Files</label>
            <div 
              className={`${modalStyles.formInput} ${isDragOver ? modalStyles.dragOver : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                if (e.dataTransfer.files) {
                  const files = Array.from(e.dataTransfer.files);
                  setSelectedFiles(files);
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              style={{ 
                border: '2px dashed #ccc', 
                borderRadius: '8px', 
                padding: '2rem', 
                textAlign: 'center', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: isDragOver ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-tertiary)',
                borderColor: isDragOver ? 'var(--primary)' : '#ccc'
              }}
            >
              <div style={{ marginBottom: '1rem', fontSize: '3rem' }}>📁</div>
              <p>Drag & drop files here or click to browse</p>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Supports PDF, CSV, XLSX files
              </p>
              <input 
                type="file" 
                ref={fileInputRef}
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
            {selectedFiles.length > 0 && (
              <div className="file-list" style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <p style={{ margin: 0 }}>Selected files ({selectedFiles.length}):</p>
                  <button 
                    type="button" 
                    onClick={() => setSelectedFiles([])}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer' }}
                  >
                    Clear all
                  </button>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px' }}>
                  {selectedFiles.map((file, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        padding: '0.5rem 1rem', 
                        borderBottom: index < selectedFiles.length - 1 ? '1px solid var(--border)' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span>{file.name}</span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={modalStyles.formActions}>
            <button type="submit" className="btn btn-primary" disabled={loading || selectedFiles.length === 0}>
              {loading ? 'Uploading...' : 'Upload Files'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeCreateModal} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
      <Modal isOpen={chatbotModalOpen} onClose={closeCreateModal} title="Create New Agent">
        <form onSubmit={handleCreateChatbot}>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Agent Name</label>
            <input 
              type="text" 
              className={modalStyles.formInput} 
              placeholder="e.g., Customer Support Bot" 
              name="name"
              value={newChatbot.name}
              onChange={handleChatbotChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Description</label>
            <textarea 
              className={`${modalStyles.formTextarea} ${modalStyles.compactTextarea}`} 
              placeholder="Describe what this chatbot will do..."
              name="description"
              value={newChatbot.description}
              onChange={handleChatbotChange}
              required
            ></textarea>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Industry Type</label>
            <select 
              className={modalStyles.formSelect}
              name="industry"
              value={newChatbot.industry}
              onChange={handleChatbotChange}
              required
            >
              <option value="">Select industry</option>
              <option value="e-commerce">E-Commerce</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Finance</option>
              <option value="technology">Technology</option>
              <option value="education">Education</option>
              <option value="tender">Tender</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Use Case</label>
            <select 
              className={modalStyles.formSelect}
              name="useCase"
              value={newChatbot.useCase}
              onChange={handleChatbotChange}
              required
            >
              <option value="">Select use case</option>
              <option value="customer_support">Customer Support</option>
              <option value="lead_generation">Lead Generation</option>
              <option value="product_recommendations">Product Recommendations</option>
              <option value="knowledge_base">Knowledge Base</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Tone</label>
            <select 
              className={modalStyles.formSelect}
              name="tone"
              value={newChatbot.tone}
              onChange={handleChatbotChange}
              required
            >
              <option value="">Select tone</option>
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="casual">Casual</option>
              <option value="technical">Technical</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Response Source</label>
            <select 
              className={modalStyles.formSelect}
              name="responseSource"
              value={newChatbot.responseSource}
              onChange={handleChatbotChange}
            >
              <option value="auto">Auto (System decides)</option>
              <option value="datasource">Datasource (DB/Files only)</option>
              <option value="llm">LLM (AI only)</option>
            </select>
          </div>
          <div className={modalStyles.formActions}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Agent'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeCreateModal} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
      <Modal isOpen={editChatbotModalOpen} onClose={() => setEditChatbotModalOpen(false)} title="Edit Agent">
        <form onSubmit={handleUpdateChatbot}>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Agent Name</label>
            <input 
              type="text" 
              className={modalStyles.formInput} 
              name="name"
              value={editingChatbot.name}
              onChange={handleEditChatbotChange}
              required
            />
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Description</label>
            <textarea 
              className={`${modalStyles.formTextarea} ${modalStyles.compactTextarea}`} 
              name="description"
              value={editingChatbot.description}
              onChange={handleEditChatbotChange}
              required
            ></textarea>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Industry Type</label>
            <select
              className={modalStyles.formSelect}
              name="industry"
              value={editingChatbot.industry}
              onChange={handleEditChatbotChange}
              required
            >
              <option value="">Select industry</option>
              <option value="e-commerce">E-Commerce</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Finance</option>
              <option value="technology">Technology</option>
              <option value="education">Education</option>
              <option value="tender">Tender</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>System Prompt</label>
            <textarea 
              className={modalStyles.formTextarea} 
              name="systemPrompt"
              value={editingChatbot.systemPrompt}
              onChange={handleEditChatbotChange}
              placeholder="Enter a custom system prompt for this agent. If left blank, the default industry prompt will be used."
            ></textarea>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Status</label>
            <select 
              className={modalStyles.formSelect}
              name="status"
              value={editingChatbot.status}
              onChange={handleEditChatbotChange}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Response Source</label>
            <select 
              className={modalStyles.formSelect}
              name="responseSource"
              value={editingChatbot.responseSource}
              onChange={handleEditChatbotChange}
            >
              <option value="auto">Auto (System decides)</option>
              <option value="datasource">Datasource (DB/Files only)</option>
              <option value="llm">LLM (AI only)</option>
            </select>
          </div>
          <div className={modalStyles.formGroup}>
            <label className={modalStyles.formLabel}>Response Preference</label>
            <div className={modalStyles.rangeLabels}>
              <span>Speed</span>
              <span>Balance</span>
              <span>Accuracy</span>
            </div>
            <input
              type="range"
              className={modalStyles.formRange}
              min={0}
              max={2}
              step={1}
              value={getPerformanceModeIndex(editingChatbot.performanceMode)}
              onChange={(e) => {
                const nextIndex = Number(e.target.value);
                setEditingChatbot(prev => ({
                  ...prev,
                  performanceMode: PERFORMANCE_MODES[nextIndex]
                }));
              }}
              style={{
                background: `linear-gradient(90deg, var(--primary) ${(getPerformanceModeIndex(editingChatbot.performanceMode) / 2) * 100}%, var(--border) ${(getPerformanceModeIndex(editingChatbot.performanceMode) / 2) * 100}%)`
              }}
            />
            <div className={modalStyles.rangeValue}>
              Selected: {editingChatbot.performanceMode.charAt(0).toUpperCase() + editingChatbot.performanceMode.slice(1)}
            </div>
          </div>
          <div className={modalStyles.formActions}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Chatbot'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditChatbotModalOpen(false)} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
      
      {/* History Modal */}
      <Modal isOpen={historyModalOpen} onClose={() => setHistoryModalOpen(false)} title="Chat History">
        {historyLoading ? (
          <div className="loading">Loading history...</div>
        ) : (
          <div className="history-modal-content">
            {!selectedSession ? (
              // Chat history view - list of sessions
              <div className="chat-history-list">
                
                {chatHistory.length === 0 ? (
                  <p>No chat history found.</p>
                ) : (
                  <div className="session-list">
                    {chatHistory.map((item: any) => (
                      <div 
                        key={item.sessionId} 
                        className="session-item"
                      >
                        <div 
                          className="session-content"
                          onClick={async () => {
                            // Fetch session history
                            await fetchSessionHistory(item.sessionId, item.chatbotId);
                            
                            // Find the chatbot associated with this session
                            const chatbot = chatbots.find(c => c.id === item.chatbotId);
                            if (chatbot) {
                              // Close the history modal
                              setHistoryModalOpen(false);
                              // Set the in-page chatbot to open the in-page chat
                              setInPageChatbot(chatbot);
                            }
                          }}
                        >
                          <div className="session-message">{item.userMessage}</div>
                          <div className="session-timestamp">
                            {new Date(item.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <button 
                          className="delete-session-btn"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this chat session?')) {
                              try {
                                await chatbotService.deleteSessionHistory(item.sessionId, item.chatbotId);
                                // Refresh history for the same chatbot context.
                                await fetchChatHistory(item.chatbotId);
                                // If this session is currently loaded in the in-page chat, clear it
                                if (selectedSession === item.sessionId) {
                                  setInPageChatbot(null);
                                }
                              } catch (err) {
                                console.error('Failed to delete chat session:', err);
                                alert('Failed to delete chat session');
                              }
                            }
                          }}
                          title="Delete session"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Session history view - detailed conversation
              <div className="session-history-detail">
                <div className="session-header">
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => setSelectedSession(null)}
                  >
                    ← Back to Sessions
                  </button>
                  <h3>Session Details</h3>
                </div>
                {sessionHistory.length === 0 ? (
                  <p>No session history found.</p>
                ) : (
                  <>
                    <div className="message-history">
                      {sessionHistory.map((item: any) => (
                        <div key={item.id} className="message-pair">
                          <div className="user-message">
                            <strong>You:</strong> {item.userMessage}
                          </div>
                          <div className="bot-message">
                            <strong>Bot:</strong> {item.botResponse}
                          </div>
                          {item.tokens && (
                            <div className="message-tokens">
                              Tokens: {item.tokens.total} (Prompt: {item.tokens.prompt}, Completion: {item.tokens.completion})
                            </div>
                          )}
                          {item.generationTimeMs && (
                            <div className="message-time">
                              Generation Time: {item.generationTimeMs}ms
                            </div>
                          )}
                          <div className="message-timestamp">
                            {new Date(item.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
      
      <DataSourceSelectionModal
        isOpen={dataSourceSelectionModalOpen}
        onClose={handleCloseDataSourceSelectionModal}
        onSave={handleSaveDataSources}
        initialSelection={initialDataSourceSelection}
        databaseSources={databaseSources}
        fileSources={fileSources}
        loading={loading}
      />

      {/* <FloatingChat hideWhenInPageChatOpen={activeTab === 'chatbots' && !!inPageChatbot} /> */}
    </div>
  );
};

export default DashboardPage;
